import React, { useState, useMemo, useEffect } from 'react';
import { Users, Calendar, Search, ChevronRight, FileText, Trash2, Edit2 } from 'lucide-react';
import BASE_API_URL from '../../../config/baseApi';
import InputField from 'components/fields/InputField';
import SelectField from 'components/fields/SelectField';

export default function ReservationForm() {
  // State management
  const [reservations, setReservations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [noOfPaxCategories, setNoOfPaxCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedReservation, setSelectedReservation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEntries, setShowEntries] = useState('10');

  const [formData, setFormData] = useState({
    reservationDate: '',
    time: '',
    daysStay: '',
    departureDate: '',
    totalPax: '',
    guestType: '',
    guestName: '',
    contactNo: '',
    email: '',
    guestFrom: '',
    agentCode: '',
    country: '',
    roomType: '',
    paxPerRoom: '',
    mealPlan: '',
    roomsNeed: '',
    rate: '',
    slRs: '',
    adults: '',
    children: '',
    infants: '',
    note: ''
  });

  // Categories state
  const [categories, setCategories] = useState({
    mealPlan: [],
    roomTypes: [],
    guestFrom: [],
    countries: [],
    noOfPax: []  // Add this
  });

  // API Functions
const fetchReservations = async () => {
  try {
    setLoading(true);
    setError(null);
    const response = await fetch(`${BASE_API_URL}/api/reservations`);
    if (!response.ok) throw new Error('Failed to fetch reservations');
    const data = await response.json();
    
    if (data.success && data.data) {
      const transformedReservations = (data.data.reservations || []).map(transformReservation);
      
      setReservations(transformedReservations);
      setCountries(data.data.countries || []);
      setNoOfPaxCategories(data.data.noOfPaxCategories || []);
    }
  } catch (err) {
    setError(err.message);
    console.error('Error fetching reservations:', err);
    setReservations([]);
  } finally {
    setLoading(false);
  }
};

  const transformReservation = (res) => ({
    id: res.id,
    reservationDate: res.reservation_date,
    time: res.reservation_time,
    daysStay: res.no_of_day,
    departureDate: res.departure_date,
    totalPax: res.total_pax_count,
    guestType: res.guest_type,
    guestName: res.guest_name,
    contactNo: res.contact_no,
    email: res.email,
    guestFrom: res.guest_from_cat,
    agentCode: res.agent_code,
    country: res.guest_country,
    roomType: res.room_type,
    paxPerRoom: res.no_of_pax,
    mealPlan: res.meal_plan,
    roomsNeed: res.rooms_need,
    rate: res.us,
    slRs: res.rs,
    adults: res.adults,
    children: res.children,
    infants: res.infants,
    note: res.description
  });

const createReservation = async (data) => {
  try {
    setError(null);
    const response = await fetch(`${BASE_API_URL}/api/reservations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create reservation');
    const result = await response.json();
    
    // Check if the response has a nested structure
    const newReservation = result.data || result;
    const transformedReservation = transformReservation(newReservation);
    setReservations([...reservations, transformedReservation]);
    return transformedReservation;
  } catch (err) {
    setError(err.message);
    console.error('Error creating reservation:', err);
    throw err;
  }
};

const updateReservation = async (id, data) => {
  try {
    setError(null);
    const response = await fetch(`${BASE_API_URL}/api/reservations/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update reservation');
    const result = await response.json();
    
    // Check if the response has a nested structure
    const updatedReservation = result.data || result;
    const transformedReservation = transformReservation(updatedReservation);
    setReservations(reservations.map(r => r.id === id ? transformedReservation : r));
    return transformedReservation;
  } catch (err) {
    setError(err.message);
    console.error('Error updating reservation:', err);
    throw err;
  }
};

  const deleteReservation = async (id) => {
    try {
      setError(null);
      const response = await fetch(`${BASE_API_URL}/api/reservations/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete reservation');
      setReservations(reservations.filter(r => r.id !== id));
    } catch (err) {
      setError(err.message);
      console.error('Error deleting reservation:', err);
      throw err;
    }
  };

  // Fetch reservations and categories on component mount
  useEffect(() => {
    fetchReservations();
    fetchCategories();
  }, []);

  // Fetch categories from database
const fetchCategories = async () => {
    try {
      const response = await fetch(`${BASE_API_URL}/api/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      
      if (data.success && data.data) {
        setCategories(data.data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Calculate departure date when reservation date or days stay changes
  useEffect(() => {
    if (formData.reservationDate && formData.daysStay) {
      const reservationDate = new Date(formData.reservationDate);
      const departureDate = new Date(reservationDate);
      departureDate.setDate(departureDate.getDate() + parseInt(formData.daysStay));
      
      const departureString = departureDate.toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        departureDate: departureString
      }));
    }
  }, [formData.reservationDate, formData.daysStay]);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleReservationClick = (reservation) => {
    setSelectedReservation(reservation.id);
    setFormData({
      reservationDate: reservation.reservationDate || '',
      time: reservation.time || '',
      daysStay: reservation.daysStay || '',
      departureDate: reservation.departureDate || '',
      totalPax: reservation.totalPax || '',
      guestType: reservation.guestType || '',
      guestName: reservation.guestName || '',
      contactNo: reservation.contactNo || '',
      email: reservation.email || '',
      guestFrom: reservation.guestFrom || '',
      agentCode: reservation.agentCode || '',
      country: reservation.country || '',
      roomType: reservation.roomType || '',
      paxPerRoom: reservation.paxPerRoom || '',
      mealPlan: reservation.mealPlan || '',
      roomsNeed: reservation.roomsNeed || '',
      rate: reservation.rate || '',
      slRs: reservation.slRs || '',
      adults: reservation.adults || '',
      children: reservation.children || '',
      infants: reservation.infants || '',
      note: reservation.note || ''
    });
    window.scrollTo(0, 0); 
  };

const handleSubmit = async () => {
  try {
    console.log("form data", formData);
    const payload = {
      reservation_date: formData.reservationDate,
      reservation_time: formData.time,
      no_of_day: formData.daysStay,
      departure_date: formData.departureDate,
      total_pax_count: formData.totalPax,
      guest_type: formData.guestType,
      guest_name: formData.guestName,
      contact_no: formData.contactNo,
      email: formData.email,
      guest_from_cat: formData.guestFrom,
      agent_code: formData.agentCode,
      guest_country: formData.country,
      room_type: formData.roomType,
      no_of_pax: formData.paxPerRoom,
      meal_plan: formData.mealPlan,
      rooms_need: formData.roomsNeed,
      us: formData.rate,
      rs: formData.slRs,
      adults: formData.adults,
      children: formData.children,
      infants: formData.infants,
      description: formData.note,
    };

    if (selectedReservation) {
      // Update existing reservation
      await updateReservation(selectedReservation, payload);
      alert('Reservation updated successfully!');
    } else {
      // Create new reservation
      await createReservation(payload);
      alert('Reservation created successfully!');
    }
    handleReset();
  } catch (err) {
    alert('Error saving reservation: ' + err.message);
    console.error('Full error:', err);
  }
};


  const handleDeleteReservation = async (id) => {
    if (window.confirm('Are you sure you want to delete this reservation?')) {
      try {
        await deleteReservation(id);
        alert('Reservation deleted successfully!');
      } catch (err) {
        alert('Error deleting reservation: ' + err.message);
      }
    }
  };

  const handleReset = () => {
    setFormData({
      reservationDate: '',
      time: '',
      daysStay: '',
      departureDate: '',
      totalPax: '',
      guestType: '',
      guestName: '',
      contactNo: '',
      email: '',
      guestFrom: '',
      agentCode: '',
      country: '',
      roomType: '',
      paxPerRoom: '',
      mealPlan: '',
      roomsNeed: '',
      rate: '',
      slRs: '',
      adults: '',
      children: '',
      infants: '',
      note: ''
    });
    setSelectedReservation(null);
  };

  const filteredReservations = useMemo(() => {
    if (!Array.isArray(reservations)) return [];
    if (!searchTerm) return reservations;
    return reservations.filter(res => 
      (res.guestName?.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
      (res.id?.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
      (res.contactNo?.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
      (res.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (res.country?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [searchTerm, reservations]);

  // const InputField = ({ label, id, type = "text", value, onChange, className = "" }) => (
  //   <div className={`flex flex-col ${className}`}>
  //     <label htmlFor={id} className="text-sm font-bold text-gray-900 dark:text-gray-300 mb-2">
  //       {label}
  //     </label>
  //     <input
  //       type={type}
  //       id={id}
  //       value={value}
  //       onChange={onChange}
  //       className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
  //     />
  //   </div>
  // );

  // const SelectField = ({ label, id, value, onChange, options, className = "" }) => (
  //   <div className={`flex flex-col ${className}`}>
  //     <label htmlFor={id} className="text-sm font-bold text-gray-900 dark:text-gray-300 mb-2">
  //       {label}
  //     </label>
  //     <select
  //       id={id}
  //       value={value}
  //       onChange={onChange}
  //       className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer transition-all"
  //     >
  //       <option value="">Select {label}</option>
  //       {options.map((opt) => (
  //         <option key={opt.value} value={opt.value}>
  //           {opt.label}
  //         </option>
  //       ))}
  //     </select>
  //   </div>
  // );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Create Reservation</h1>
          <p className="text-gray-600 dark:text-gray-400">Fill in the details to create a new reservation</p>
        </div>

        <div className="space-y-6">
              {/* Reservation Dates Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <InputField
                    label="Reservation Date"
                    id="reservationDate"
                    type="date"
                    value={formData.reservationDate}
                    onChange={handleChange('reservationDate')}
                  />
                  <InputField
                    label="Time"
                    id="time"
                    type="time"
                    value={formData.time}
                    onChange={handleChange('time')}
                  />
                  <InputField
                    label="No of Days Stay"
                    id="daysStay"
                    type="number"
                    value={formData.daysStay}
                    onChange={handleChange('daysStay')}
                  />
                  <InputField
                    label="Departure Date"
                    id="departureDate"
                    type="date"
                    value={formData.departureDate}
                    onChange={handleChange('departureDate')}
                  />
                  <InputField
                    label="Total PAX Count"
                    id="totalPax"
                    type="number"
                    value={formData.totalPax}
                    onChange={handleChange('totalPax')}
                  />
                </div>
              </div>

              {/* Guest Information Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <SelectField
                    label="Guest Type"
                    id="guestType"
                    value={formData.guestType}
                    onChange={handleChange('guestType')}
                    options={[
                      { value: 'Mr.', label: 'Mr.' },
                      { value: 'Mrs.', label: 'Mrs.' },
                      { value: 'Ms.', label: 'Ms.' },
                      { value: 'Dr.', label: 'Dr.' }
                    ]}
                  />
                  <InputField
                    label="Guest Name"
                    id="guestName"
                    value={formData.guestName}
                    onChange={handleChange('guestName')}
                  />
                  <InputField
                    label="Contact No"
                    id="contactNo"
                    type="tel"
                    value={formData.contactNo}
                    onChange={handleChange('contactNo')}
                  />
                  <InputField
                    label="Email"
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange('email')}
                  />
                </div>
              </div>

              {/* Booking Source Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <SelectField
                    label="Guest From"
                    id="guestFrom"
                    value={formData.guestFrom}
                    onChange={handleChange('guestFrom')}
                    options={(categories.guestFrom || []).map(item => ({ value: item.name, label: item.name }))}
                  />
                  <InputField
                    label="Agent Code"
                    id="agentCode"
                    value={formData.agentCode}
                    onChange={handleChange('agentCode')}
                  />
                  <SelectField
                    label="Country"
                    id="country"
                    value={formData.country}
                    onChange={handleChange('country')}
                    options={countries.map(item => ({ 
                      value: item.country_name, 
                      label: item.country_name 
                    }))}
                  />
                </div>
              </div>

              {/* Room Details Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <SelectField
                    label="Room Type"
                    id="roomType"
                    value={formData.roomType}
                    onChange={handleChange('roomType')}
                    options={(categories.roomTypes || []).map(item => ({ value: item.name, label: item.name }))}
                  />
                  <SelectField
                    label="No of Pax (Per Room)"
                    id="paxPerRoom"
                    value={formData.paxPerRoom}
                    onChange={handleChange('paxPerRoom')}
                    options={noOfPaxCategories.map(item => ({ 
                      value: item.cat_name, 
                      label: item.cat_name 
                    }))}
                  />
                  <SelectField
                    label="Meal Plan"
                    id="mealPlan"
                    value={formData.mealPlan}
                    onChange={handleChange('mealPlan')}
                    options={(categories.mealPlan || []).map(item => ({ value: item.name, label: item.name }))}
                  />
                </div>
              </div>

              {/* Pricing Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField
                    label="Rooms Need"
                    id="roomsNeed"
                    type="number"
                    value={formData.roomsNeed}
                    onChange={handleChange('roomsNeed')}
                  />
                  <InputField
                    label="Rate : US $"
                    id="rate"
                    inputmode="decimal"
                    type="text"
                    value={formData.rate}
                    onChange={handleChange('rate')}
                  />
                  <InputField
                    label="SL Rs."
                    id="slRs"
                    inputmode="decimal"
                    type="text"
                    value={formData.slRs}
                    onChange={handleChange('slRs')}
                  />
                </div>
              </div>

              {/* Guest Count & Notes Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <InputField
                    label="Adults"
                    id="adults"
                    type="number"
                    value={formData.adults}
                    onChange={handleChange('adults')}
                  />
                  <InputField
                    label="Children"
                    id="children"
                    type="number"
                    value={formData.children}
                    onChange={handleChange('children')}
                  />
                  <InputField
                    label="Infants"
                    id="infants"
                    type="number"
                    value={formData.infants}
                    onChange={handleChange('infants')}
                  />
                  <InputField
                    label="Note"
                    id="note"
                    value={formData.note}
                    onChange={handleChange('note')}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center py-6">
                {error && (
                  <div className="w-full px-4 py-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg">
                    {error}
                  </div>
                )}
                <button 
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-10 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : selectedReservation ? 'Update' : 'Submit'}
                </button>
                <button 
                  onClick={handleReset}
                  disabled={loading}
                  className="px-10 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reset
                </button>
              </div>

              {/* Reservations Table */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 dark:text-gray-400">Show</span>
                      <select 
                        value={showEntries}
                        onChange={(e) => setShowEntries(e.target.value)}
                        className="px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                      </select>
                      <span className="text-sm text-gray-700 dark:text-gray-400">entries</span>
                    </div>
                    <div className="relative">
                      <span className="text-sm text-gray-700 dark:text-gray-400 mr-2">Search:</span>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Guest name, email, contact, country..."
                        className="px-4 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/50">
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">#</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Guest Name</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Guest Country</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Contact No</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Agent Code</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Guest From</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center text-gray-600 dark:text-gray-400">
                            <p>Loading reservations...</p>
                          </td>
                        </tr>
                      ) : filteredReservations.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center text-gray-600 dark:text-gray-400">
                            <div className="flex flex-col items-center justify-center">
                              <FileText className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" />
                              <p className="text-sm">Showing 0 to 0 of 0 entries</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredReservations.slice(0, parseInt(showEntries)).map((reservation, index) => (
                          <tr key={reservation.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{index + 1}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{reservation.guestName || '-'}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{reservation.country || '-'}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{reservation.contactNo || '-'}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{reservation.email || '-'}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{reservation.agentCode || '-'}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{reservation.guestFrom || '-'}</td>
                            <td className="px-6 py-4 text-sm space-x-2 flex">
                              <button
                                onClick={() => handleReservationClick(reservation)}
                                className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteReservation(reservation.id)}
                                className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {filteredReservations.length === 0 ? 0 : 1} to {Math.min(parseInt(showEntries), filteredReservations.length)} of {filteredReservations.length} entries
                  </p>
                  <div className="flex gap-2">
                    <button className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                      Prev
                    </button>
                    <button className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </div>
    
  );
}